import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { faCalendarDays, faLayerGroup, faTag } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment-timezone';
import { Period } from '@spike/model';
import {
    BenefitUsageFilters,
    BenefitUsageItemType,
    BenefitUsagePage,
    fetchBenefitUsageThunk
} from '@spike/memberships-action';
import DefaultHeader from 'components/UI/V2/DefaultHeader/DefaultHeader';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import { SelectDropdown, SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import Spinner from 'components/UI/V3/Spinner';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import { RootState } from 'store';
import BenefitUsageExportCSV from './BenefitUsageExportCSV';
import {
    benefitTypeLabel,
    formatDiscount,
    itemTypeLabel,
    planTypeLabel,
    serviceCategoryLabel
} from './benefitUsageLabels';
import styles from './BenefitUsage.module.scss';

type ItemTypeFilter = BenefitUsageItemType | 'all';

const ITEM_TYPE_OPTIONS: Array<SelectDropdownOption<ItemTypeFilter>> = [
    { id: 'all', label: 'All items' },
    { id: 'service', label: 'Service' },
    { id: 'addon', label: 'Add-on' },
    { id: 'product', label: 'Product' }
];

// Same catalog as the backend business areas.
const CATEGORY_OPTIONS: Array<SelectDropdownOption<string | null>> = [
    { id: null, label: 'All categories' },
    ...['grooming', 'dental', 'daycare', 'boarding', 'evaluation', 'transportation', 'training', 'walking'].map(
        slug => ({ id: slug, label: serviceCategoryLabel(slug) })
    )
];

const DATE_FORMAT = 'YYYY-MM-DD';

const BenefitUsage: React.FC = () => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, allTimeOption } = useDateRangeOptions();

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });
    const [itemType, setItemType] = useState<ItemTypeFilter>('all');
    const [category, setCategory] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [data, setData] = useState<BenefitUsagePage | null>(null);
    const [loading, setLoading] = useState(true);
    const requestId = useRef(0);

    // The category only applies to services and add-ons, products do not have one.
    const categoryEnabled = itemType === 'service' || itemType === 'addon';

    const filters: BenefitUsageFilters = useMemo(
        () => ({
            from: period ? period.from.clone().tz(timeZone).format(DATE_FORMAT) : undefined,
            to: period ? period.to.clone().tz(timeZone).format(DATE_FORMAT) : undefined,
            itemType,
            serviceCategory: categoryEnabled && category ? category : undefined
        }),
        [period, timeZone, itemType, category, categoryEnabled]
    );

    useEffect(() => {
        const currentRequest = ++requestId.current;
        setLoading(true);

        dispatch(fetchBenefitUsageThunk(apiClientWrapper, page, size, filters))
            .then(result => {
                if (currentRequest === requestId.current) setData(result);
            })
            .catch(() => {
                // the thunk already shows the error alert
                if (currentRequest === requestId.current) setData(null);
            })
            .finally(() => {
                if (currentRequest === requestId.current) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page, size]);

    const changePeriodHandler = (value: Period | null) => {
        setPeriod(value ? { from: value.from.clone(), to: value.to.clone() } : null);
        setPage(1);
    };

    const changeItemTypeHandler = (option: SelectDropdownOption<ItemTypeFilter> | null) => {
        setItemType(option?.id ?? 'all');
        setCategory(null);
        setPage(1);
    };

    const changeCategoryHandler = (option: SelectDropdownOption<string | null> | null) => {
        setCategory(option?.id ?? null);
        setPage(1);
    };

    const changeSizeHandler = (nextSize: number) => {
        setSize(nextSize);
        setPage(1);
    };

    const rows = data?.rows ?? [];

    const renderBody = () => {
        if (loading && !data) {
            return (
                <div className={styles.message}>
                    <Spinner />
                </div>
            );
        }

        if (rows.length === 0) {
            return (
                <div className={styles.message}>
                    <h3 className={styles.messageTitle}>No benefit usage found</h3>
                    <p className={styles.messageText}>
                        There are no memberships or packages benefits used with these filters.
                    </p>
                </div>
            );
        }

        return (
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.headerCell}>Date</th>
                            <th className={styles.headerCell}>Client</th>
                            <th className={styles.headerCell}>Benefit</th>
                            <th className={styles.headerCell}>Type</th>
                            <th className={styles.headerCell}>Category</th>
                            <th className={styles.headerCell}>Membership / Package</th>
                            <th className={styles.headerCell}>Usage</th>
                            <th className={`${styles.headerCell} ${styles.number}`}>Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.orderItemId}>
                                <td className={`${styles.cell} ${styles.nowrap}`}>
                                    {moment.tz(row.usedAt, timeZone).format('MMM D, YYYY')}
                                    <div className={styles.muted}>
                                        {moment.tz(row.usedAt, timeZone).format('h:mm A')}
                                    </div>
                                </td>
                                <td className={styles.cell}>
                                    {row.clientName}
                                    {row.petName && <div className={styles.muted}>{row.petName}</div>}
                                </td>
                                <td className={styles.cell}>{row.benefitName}</td>
                                <td className={styles.cell}>{itemTypeLabel(row.itemType)}</td>
                                <td className={styles.cell}>{serviceCategoryLabel(row.serviceCategory)}</td>
                                <td className={styles.cell}>
                                    {row.membershipName}
                                    <div className={styles.muted}>{planTypeLabel(row.enrollmentType)}</div>
                                </td>
                                <td className={`${styles.cell} ${styles.nowrap}`}>
                                    {benefitTypeLabel(row.benefitKind)}
                                    {row.benefitKind === 'discount' && (
                                        <div className={styles.discount}>-{formatDiscount(row.discountAmount)}</div>
                                    )}
                                </td>
                                <td className={`${styles.cell} ${styles.number}`}>{row.quantity}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={styles.root}>
            <div className={styles.head}>
                <DefaultHeader
                    title="Benefit Usage"
                    textBelowTitle="Benefits used and discounts applied through memberships and packages."
                    className={styles.header}
                />
                <div className={styles.toolbar}>
                    <DateRangePickerDropdown
                        timeZone={timeZone}
                        value={period}
                        onChange={changePeriodHandler}
                        options={[todayOption, thisMonthOption, last30DaysOption, allTimeOption]}
                        buttonProps={{
                            'variant': 'outlined',
                            'leftIcon': faCalendarDays,
                            'aria-label': 'Date range filter'
                        }}
                        dropdownProps={{ align: 'start' }}
                    />
                    <SelectDropdown<ItemTypeFilter>
                        options={ITEM_TYPE_OPTIONS}
                        value={ITEM_TYPE_OPTIONS.find(option => option.id === itemType) ?? ITEM_TYPE_OPTIONS[0]}
                        onChange={changeItemTypeHandler}
                        buttonProps={{ 'variant': 'outlined', 'leftIcon': faTag, 'aria-label': 'Item type filter' }}
                    />
                    {categoryEnabled && (
                        <SelectDropdown<string | null>
                            options={CATEGORY_OPTIONS}
                            value={CATEGORY_OPTIONS.find(option => option.id === category) ?? CATEGORY_OPTIONS[0]}
                            onChange={changeCategoryHandler}
                            buttonProps={{
                                'variant': 'outlined',
                                'leftIcon': faLayerGroup,
                                'aria-label': 'Service category filter'
                            }}
                        />
                    )}
                    <div className={styles.spacer} />
                    {data && (
                        <span className={styles.summary}>
                            {data.totalUses} {data.totalUses === 1 ? 'use' : 'uses'}
                        </span>
                    )}
                    <BenefitUsageExportCSV filters={filters} />
                </div>
            </div>

            {renderBody()}

            {data && rows.length > 0 && (
                <div className={styles.paginationWrap}>
                    <Pagination
                        page={data.currentPage}
                        totalPages={data.totalPages}
                        size={data.perPage}
                        isLoading={loading}
                        onPageChange={setPage}
                        onSizeChange={changeSizeHandler}
                    />
                </div>
            )}
        </div>
    );
};

export default BenefitUsage;
